import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { ColumnFeatures } from "../../features";
import SplitBarCell, { SplitBarCellData } from "../../split-bar-cell";
import GridDataTooltip from "../../tooltips/split-bar-tooltip";
import SplitBreakdownTooltip, { SplitTooltipOptions } from "../../tooltips/split-breakdown-tooltip";
import PopperCell from "../../popper-cell";
import { useRef } from "react";
import CompetitorsTooltip from "../../tooltips/competitors";
import { Avatar, AvatarGroup, Typography, Stack } from "@mui/material";
import { formatNumber } from "src/utils/format-number";


export function buildSplitCell(
    column: GridColDef & {
        features: ColumnFeatures[],
        headerName: string,
        headerTooltip: string,
        transform: (params: GridRenderCellParams) => any,
        format: (value: number) => string,
        tooltip?: SplitTooltipOptions
    },
    popperRef: { current: { rowId: string | null } },
    /** breakdown: the Search terms (Figma) tooltip; otherwise the former one (Competitors grid). */
    { breakdown = false }: { breakdown?: boolean } = {}
) {
    const { format, transform, tooltip } = column;

    return function SplitCell(params: GridRenderCellParams) {
        const split = transform(params);
        // Count tooltips list every source, zero included; the cell's bar only draws the ones that have some
        const bar = tooltip?.values === 'count' ? { ...split, data: split.data.filter((item: SplitBarCellData) => (item.help ?? 0) > 0) } : split;

        return (
            <PopperCell
                popperRef={popperRef}
                rowId={params.row.id}
                popper={
                    breakdown ? (
                        <SplitBreakdownTooltip data={split.data} total={split.total} format={format} title={column.headerName} {...tooltip} />
                    ) : (
                        <GridDataTooltip data={split.data} total={split.total} format={format} title={column.headerName} />
                    )
                }
            >
                <SplitBarCell {...bar} format={format} />
            </PopperCell>
        );
    };
}

export function buildRankCell(column: GridColDef & { features: ColumnFeatures[], headerName: string, headerTooltip: string, transform: (params: GridRenderCellParams) => any, format: (value: number) => string }, popperRef: {current: {rowId: string | null}}) {
    const { format, transform } = column;
    return function RankCell(params: GridRenderCellParams) {
        return (
            <PopperCell
                popperRef={popperRef}
                rowId={params.row.id}
                popper={<CompetitorsTooltip competitors={transform(params.row[column.field])} title={column.field} />}
            >
                <AvatarGroup
                    max={4}
                    sx={{
                        '& .MuiAvatar-root': {
                            border: 'none',
                            width: 24,
                            height: 24,
                        },
                    }}
                >
                    {[...(params.row[column.field] || [])]
                        ?.sort((a: any, b: any) => b.rate_field - a.rate_field)
                        .map((c: any, i: number) => (
                            <Avatar
                                key={i}
                                alt={c.domain_name}
                                src={`https://www.google.com/s2/favicons?domain=${c.domain_name}&sz=32`}
                            />
                        ))}
                </AvatarGroup>
            </PopperCell>
        );
    };
}

export function buildSimpleBarCell(column: GridColDef & { color: string, features: ColumnFeatures[], headerName: string, headerTooltip: string, transform: (params: GridRenderCellParams) => any, format: (value: any) => string }) {
    const { format = formatNumber, transform } = column;
    return function SimpleBarCell(params: GridRenderCellParams) {
        return (
            <Stack direction="row" alignItems="center" spacing={1} py={1} sx={{ width: '100%', height: '100%' }}>

                <Typography variant="body2">
                    {format(params.row[column.field])}
                </Typography>
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    py={1}
                    sx={{ width: '100%', height: '100%' }}
                >
                    <Stack
                        sx={{
                            width: `${params.row[column.field]}%`,
                            height: '8px',
                            backgroundColor: column.color,
                            borderRadius: '6px',
                        }}
                    />
                </Stack>
            </Stack>
        );
    };
}